import React, { useState, useEffect } from 'react';
import { Brain, Lightbulb, TrendingUp, Users, ArrowRight, Star, Calendar, X, BookOpen } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface SmartCreateAssistantProps {
  onSuggestionSelect: (suggestion: ContentSuggestion) => void;
  onClose: () => void;
}

interface ContentSuggestion {
  id: string;
  type: 'trending' | 'audience' | 'personal' | 'seasonal' | 'follow-up' | 'series';
  title: string;
  description: string;
  confidence: number;
  estimatedEngagement: number;
  timeToCreate: string;
  difficulty: 'easy' | 'medium' | 'hard';
  tags: string[];
  template?: string;
  audienceInsights?: {
    interest: number;
    engagement: number;
    growth: number;
  };
  trendingData?: {
    searchVolume: number;
    socialMentions: number;
    competitorActivity: number;
  };
}

interface AudienceInsight {
  topic: string;
  interest: number;
  engagement: number;
  growth: number;
  lastEngagement: Date;
}

interface TrendingTopic {
  topic: string;
  searchVolume: number;
  socialMentions: number;
  competitorActivity: number;
  relevance: number;
}

const SmartCreateAssistant: React.FC<SmartCreateAssistantProps> = ({
  onSuggestionSelect,
  onClose
}) => {
  const { currentUser } = useAuth();
  const [suggestions, setSuggestions] = useState<ContentSuggestion[]>([]);
  const [audienceInsights, setAudienceInsights] = useState<AudienceInsight[]>([]);
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'suggestions' | 'trending' | 'audience' | 'personal'>('suggestions');

  useEffect(() => {
    loadSmartSuggestions();
  }, []);

  const loadSmartSuggestions = async () => {
    setLoading(true);
    try {
      // Mock data - in real app, this would come from AI analysis
      const mockSuggestions: ContentSuggestion[] = [
        {
          id: '1',
          type: 'trending',
          title: 'The Future of AI in Content Creation',
          description: 'Based on trending searches and your audience interests in AI topics',
          confidence: 92,
          estimatedEngagement: 85,
          timeToCreate: '45 min',
          difficulty: 'medium',
          tags: ['AI', 'Content Creation', 'Technology', 'Future'],
          audienceInsights: {
            interest: 88,
            engagement: 92,
            growth: 15
          },
          trendingData: {
            searchVolume: 8500,
            socialMentions: 1200,
            competitorActivity: 45
          }
        },
        {
          id: '2',
          type: 'audience',
          title: 'Weekly Newsletter: Tech Updates',
          description: 'Your audience consistently engages with weekly tech roundups',
          confidence: 78,
          estimatedEngagement: 72,
          timeToCreate: '30 min',
          difficulty: 'easy',
          tags: ['Newsletter', 'Tech', 'Weekly', 'Updates'],
          audienceInsights: {
            interest: 75,
            engagement: 78,
            growth: 8
          }
        },
        {
          id: '3',
          type: 'follow-up',
          title: 'Part 2: Advanced Email Marketing Strategies',
          description: 'Continue the conversation from your popular email marketing post',
          confidence: 85,
          estimatedEngagement: 68,
          timeToCreate: '60 min',
          difficulty: 'medium',
          tags: ['Email Marketing', 'Advanced', 'Strategy', 'Follow-up'],
          audienceInsights: {
            interest: 82,
            engagement: 85,
            growth: 12
          }
        },
        {
          id: '4',
          type: 'seasonal',
          title: 'Holiday Marketing Campaign Ideas',
          description: 'Perfect timing for holiday content based on seasonal trends',
          confidence: 70,
          estimatedEngagement: 65,
          timeToCreate: '40 min',
          difficulty: 'easy',
          tags: ['Holiday', 'Marketing', 'Campaign', 'Seasonal'],
          audienceInsights: {
            interest: 68,
            engagement: 70,
            growth: 5
          }
        }
      ];

      const mockAudienceInsights: AudienceInsight[] = [
        {
          topic: 'AI and Machine Learning',
          interest: 88,
          engagement: 92,
          growth: 15,
          lastEngagement: new Date()
        },
        {
          topic: 'Email Marketing',
          interest: 75,
          engagement: 78,
          growth: 8,
          lastEngagement: new Date(Date.now() - 86400000)
        },
        {
          topic: 'Content Strategy',
          interest: 82,
          engagement: 85,
          growth: 12,
          lastEngagement: new Date(Date.now() - 172800000)
        }
      ];

      const mockTrendingTopics: TrendingTopic[] = [
        {
          topic: 'AI Content Creation',
          searchVolume: 8500,
          socialMentions: 1200,
          competitorActivity: 45,
          relevance: 92
        },
        {
          topic: 'Email Marketing Automation',
          searchVolume: 6200,
          socialMentions: 890,
          competitorActivity: 32,
          relevance: 78
        },
        {
          topic: 'Newsletter Growth Hacks',
          searchVolume: 4800,
          socialMentions: 650,
          competitorActivity: 28,
          relevance: 85
        }
      ];

      setSuggestions(mockSuggestions);
      setAudienceInsights(mockAudienceInsights);
      setTrendingTopics(mockTrendingTopics);
    } catch (error) {
      console.error('Error loading smart suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'trending': return TrendingUp;
      case 'audience': return Users;
      case 'personal': return Star;
      case 'seasonal': return Calendar;
      case 'follow-up': return ArrowRight;
      case 'series': return BookOpen;
      default: return Lightbulb;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'trending': return 'text-orange-600 bg-orange-100';
      case 'audience': return 'text-blue-600 bg-blue-100';
      case 'personal': return 'text-purple-600 bg-purple-100';
      case 'seasonal': return 'text-green-600 bg-green-100';
      case 'follow-up': return 'text-indigo-600 bg-indigo-100';
      case 'series': return 'text-pink-600 bg-pink-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getEngagementColor = (engagement: number) => {
    if (engagement >= 80) return 'text-green-600';
    if (engagement >= 60) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const tabs = [
    { id: 'suggestions', label: 'Smart Suggestions', icon: Brain },
    { id: 'trending', label: 'Trending Topics', icon: TrendingUp },
    { id: 'audience', label: 'Audience Insights', icon: Users },
    { id: 'personal', label: 'Personal Ideas', icon: Star }
  ];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600 dark:text-gray-400">Analyzing your content and audience...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-none shadow-2xl w-full h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Smart Create Assistant</h2>
                <p className="text-gray-600 dark:text-gray-400">AI-powered content suggestions based on your audience and trends</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1 sm:gap-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
          {activeTab === 'suggestions' && (
            <div className="space-y-4">
              {suggestions.map((suggestion) => {
                const TypeIcon = getTypeIcon(suggestion.type);
                return (
                  <div
                    key={suggestion.id}
                    onClick={() => onSuggestionSelect(suggestion)}
                    className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-lg transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg ${getTypeColor(suggestion.type)} flex items-center justify-center`}>
                          <TypeIcon className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{suggestion.title}</h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">{suggestion.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {suggestion.confidence}% match
                        </span>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                      <div className="text-center">
                        <div className={`text-2xl font-bold ${getEngagementColor(suggestion.estimatedEngagement)}`}>
                          {suggestion.estimatedEngagement}%
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Engagement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {suggestion.timeToCreate}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Time to Create</div>
                      </div>
                      <div className="text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(suggestion.difficulty)}`}>
                          {suggestion.difficulty}
                        </span>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Difficulty</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {suggestion.tags.length}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Tags</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {suggestion.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'trending' && (
            <div className="space-y-4">
              {trendingTopics.map((topic, index) => (
                <div key={index} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{topic.topic}</h3>
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {topic.relevance}% relevant
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Search Volume</div>
                      <div className="font-medium">{topic.searchVolume.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Social Mentions</div>
                      <div className="font-medium">{topic.socialMentions.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">Competitor Activity</div>
                      <div className="font-medium">{topic.competitorActivity}%</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'audience' && (
            <div className="space-y-4">
              {audienceInsights.map((insight, index) => (
                <div key={index} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">{insight.topic}</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{insight.interest}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Interest</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{insight.engagement}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Engagement</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">+{insight.growth}%</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Growth</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'personal' && (
            <div className="text-center py-8">
              <Star className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Personal Ideas</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Based on your content history and personal interests
              </p>
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Generate Personal Ideas
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartCreateAssistant;
